import isEqual from 'lodash/isEqual';
import { Input, Concept, Filter, Rank, Data, Annotation, Image } from 'api/grpc/resources';
import { ParsedUrlQuery } from 'querystring';
import { MultiSearchResponse } from 'api/grpc/service';
import { either } from 'fp-ts';
import { pipe } from 'utils/fp';
import { SearchQuery } from 'utils/storage/inputsearch';
import { Instance } from 'mobx-state-tree';
import { compileAPIFilters } from 'modules/InputsManager/helpers';
import { InputRank, TextSimilarityRank } from 'modules/InputsManager/models/InputRank';
import { AsymmetricalSearchPromptT } from 'modules/InputsManager/types';

export const getRespPage = (listE: either.Either<Error, MultiSearchResponse>): Input[] | null => {
  return pipe(
    listE,
    either.match(
      // onLeft
      () => {
        console.warn('getRespPage: something went wrong');
        return null;
      },
      // onRight
      (resp) => {
        return resp.getHitsList().map((hit) => {
          // non-null assertion is fine because the API always returns an input for each hit
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return hit.getInput()!;
        });
      },
    ),
  );
};

export const getFiltersFromSearch = (
  search: SearchQuery,
  isAnnotationsSearch: boolean = false,
  isStatusFilterEqualsToAnnotation: boolean = true,
): Filter[] => {
  const metadata = search.metadata.length > 0 ? JSON.parse(search.metadata[0]) : undefined;
  const filters = compileAPIFilters({
    metadataFilter: !isAnnotationsSearch ? metadata : undefined,
    unlabeledFilter: search.unlabeledFilter,
    unassignedFilter: search.unassignedFilter,
    conceptFilters: search.conceptFilters,
    datasetFilters: search.datasets,
    inputTypeFilters: search.inputTypeFilters,
    conceptsFilterOperator: search.conceptsFilterOperator,
    statusFilterValue: search.statusFilterValue,
    labelerFilterValue: search.labelerFilterValue,
    allSearch: !isAnnotationsSearch,
    allowStatusFilterForAnnotation: isStatusFilterEqualsToAnnotation,
  });

  return filters;
};

const _createConceptRank = (id: string, name: string): Rank => {
  const concept = new Concept();
  concept.setName(name);
  concept.setId(id);
  concept.setValue(1);

  const data = new Data();
  data.setConceptsList([concept]);

  const annotation = new Annotation();
  annotation.setData(data);

  const rank = new Rank();
  rank.setAnnotation(annotation);

  return rank;
};

const createRankFromInputId = (inputId: string): Rank => {
  const annotation = new Annotation();
  const rank = new Rank();

  annotation.setInputId(inputId);
  rank.setAnnotation(annotation);

  return rank;
};

const createRankFromBase64 = (base64: string): Rank => {
  const annotation = new Annotation();
  const rank = new Rank();
  const data = new Data();
  const image = new Image();

  image.setBase64(base64);
  data.setImage(image);
  annotation.setData(data);
  rank.setAnnotation(annotation);

  return rank;
};

const getRanksFromSearchAndUrl = (search: SearchQuery, query?: ParsedUrlQuery): Rank[] => {
  /// TODO: ideally, this should be shared between this and InputManager store
  const ranks = [] as Rank[];
  if (search.smartSearch?.appConcepts.length > 0 || search.smartSearch?.publicConcepts.length > 0) {
    const allConcepts = search.smartSearch.appConcepts.concat(search.smartSearch.publicConcepts);
    ranks.push(...allConcepts.map((concept) => _createConceptRank(concept.id, concept.name)));
  }

  if (query?.rankId) {
    ranks.push(createRankFromInputId(query.rankId as string));
  }

  if (search?.base64) {
    for (const base64 of search.base64) {
      ranks.push(createRankFromBase64(base64 as string));
    }
  }

  // TODO: other kinds of ranks like input ID, base64 need to be added here...
  return ranks;
};

export const getFinalRanks = async (
  search: SearchQuery,
  ranks: Instance<typeof InputRank>[],
  asymmetricalSearchPrompt: AsymmetricalSearchPromptT,
  query?: ParsedUrlQuery,
): Promise<Rank[]> => {
  const ranksFromUrl = getRanksFromSearchAndUrl(search, query);
  const apiRanks = ranks.map((rank) => rank.toAPI());

  const uniqueApiRanks = apiRanks.filter((rank) => {
    const hasMatch = ranksFromUrl.some((rankItem) => isEqual(rankItem, rank));
    return !hasMatch;
  });
  let results = [...ranksFromUrl, ...uniqueApiRanks];

  if (!asymmetricalSearchPrompt.selected || !asymmetricalSearchPrompt.value) return results;

  results.forEach((rank) => {
    const text = rank.getAnnotation()?.getData()?.getText()?.getRaw(); // Getting the raw text from the rank
    if (!text) return;
    results = [
      TextSimilarityRank.create({
        inputText: `${asymmetricalSearchPrompt.value}${text}`, // Adding the asymmetrical search prompt to the text of the rank
      }).toAPI(),
      ...results,
    ];
  });

  return results;
};

export const eventOriginatesFromCheckbox = (e: React.MouseEvent | React.KeyboardEvent): boolean => {
  const el = e.target as HTMLElement;
  return el.tagName === 'INPUT' && el.getAttribute('type') === 'checkbox';
};
